import { io, Socket } from 'socket.io-client'

import type {
  AuthPayload,
  ClientErrorPayload,
  ClientToServerEvents,
  ServerToClientEvents,
  SyncPayload,
} from '@collector/queue-gateway-shared-types'

import type { ClientConfig } from './interfaces/client-config.interface.js'
import type { MessageConfig } from './interfaces/event-config.interface.js'
import type { ClientTopic, TopicMessage, TopicType } from './topics.types.js'

export class QueueRabbitClient {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents>
  private disconnected = false

  public constructor(config: ClientConfig) {
    this.socket = io(config.url as string, {
      auth: config.auth,
      timeout: 5000,
      forceNew: true,
      rememberUpgrade: true,
      closeOnBeforeunload: true,
      transports: ['websocket', 'polling'],
    })

    this.socket.on('unsubscribe', (payload) => this.socket.off(payload.topic))
  }

  public onConnect(onConnect: () => void, onReconnect?: () => void): void {
    this.socket.on('connect', async () => {
      onConnect()

      if (this.disconnected) {
        this.disconnected = false
        onReconnect?.()
      }
    })
  }

  public onConnectError(onConnectError: (error: Error) => void): void {
    this.socket.on('connect_error', (error) => {
      if (!this.disconnected) {
        this.disconnected = true
        onConnectError(error)
      }
    })
  }

  public onStatusChange(onStatusChange: (connected: boolean) => void): void {
    const watchForConnectionProblem = (): void => {
      this.socket.io.once('reconnect_error', () => {
        onStatusChange(false)
        watchForRecovery()
      })
    }

    const watchForRecovery = (): void => {
      this.socket.io.once('reconnect', () => {
        onStatusChange(true)
        watchForConnectionProblem()
      })
    }
    watchForConnectionProblem()
  }

  public onClientError(onError: (error: ClientErrorPayload) => void): void {
    this.socket.on('clientError', async (error, ack) => {
      onError(error)
      ack()
    })
  }

  public onDisconnect(
    onDisconnect: (reason: Socket.DisconnectReason) => void,
  ): void {
    this.socket.on('disconnect', (reason: Socket.DisconnectReason) => {
      this.disconnected = true
      onDisconnect(reason)
    })
  }

  public setAuth(payload: AuthPayload): void {
    this.socket.auth = payload
    this.socket.emit('auth', payload)
  }

  public subscribe<K extends TopicType>(
    topic: ClientTopic<K>,
    config: SyncPayload & MessageConfig<TopicMessage[K]>,
  ): () => void {
    const handler = (batch: unknown[]): void => {
      batch.forEach((p) => config.onMessage?.(p as TopicMessage[K]))
      config.onMessageBatch?.(batch as TopicMessage[K][])
    }

    this.socket.on(topic as string, handler)
    this.socket.emit(
      'subscribe',
      config.sync ? { topic, sync: config.sync } : { topic },
    )

    return () => {
      if (this.socket.connected) {
        this.socket.emit('unsubscribe', { topic })
      }
      this.socket.off(topic as string, handler)
    }
  }

  public destroy(): void {
    this.socket.disconnect()
  }
}
